import { useCallback, useMemo } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth } from './useAuth';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const CHAT_READ_STATUS_KEY = 'chat_read_status';

interface ChatReadStatus {
  [chatId: string]: string; // chatId -> ISO timestamp of last read
}

export function useChatReadStatus() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Use React Query to manage read status state - shared across all components
  const { data: readStatus = {} } = useQuery({
    queryKey: ['chat-read-status', user?.id],
    queryFn: async (): Promise<ChatReadStatus> => {
      if (!user?.id) return {};
      
      try {
        const key = `${CHAT_READ_STATUS_KEY}_${user.id}`;
        const stored = await AsyncStorage.getItem(key);
        console.log('📖 [READ_STATUS] Loaded from storage:', stored ? 'found data' : 'empty');
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (error) {
        console.error('Error loading chat read status:', error);
      }
      return {};
    },
    enabled: !!user?.id,
    staleTime: 0, // Always consider data fresh enough to trigger re-renders
    gcTime: 1000 * 60 * 60, // Keep in cache for 1 hour
  });

  // Mark a chat as read (call this when user opens a chat)
  const markChatAsRead = useCallback(async (chatId: string) => {
    if (!user?.id) return;
    
    const now = new Date().toISOString();
    console.log('📖 [READ_STATUS] Marking chat as read:', chatId, 'at', now);
    
    // Get current status from cache
    const currentStatus = queryClient.getQueryData<ChatReadStatus>(['chat-read-status', user.id]) || {};
    const newStatus = { ...currentStatus, [chatId]: now };
    
    // Update the React Query cache immediately - this triggers re-renders
    queryClient.setQueryData(['chat-read-status', user.id], newStatus);
    
    // Save to AsyncStorage for persistence
    try {
      const key = `${CHAT_READ_STATUS_KEY}_${user.id}`;
      await AsyncStorage.setItem(key, JSON.stringify(newStatus));
      console.log('📖 [READ_STATUS] Saved to storage');
    } catch (error) {
      console.error('Error saving chat read status:', error);
    }
  }, [user?.id, queryClient]);

  // Check if a chat has unread messages - memoized for performance
  const hasUnreadMessages = useCallback((
    chatId: string, 
    lastMessageTime: string | undefined,
    lastMessageSenderId: string | undefined,
    currentUserId: string | undefined
  ): boolean => {
    // If no last message or message is from current user, not unread
    if (!lastMessageTime || !lastMessageSenderId || lastMessageSenderId === currentUserId) {
      return false;
    }

    const lastReadTime = readStatus[chatId];
    
    // If never read this chat, it's unread
    if (!lastReadTime) {
      return true;
    }

    // Compare timestamps - unread if last message is after last read
    const messageTime = new Date(lastMessageTime).getTime();
    const readTime = new Date(lastReadTime).getTime();
    const isUnread = messageTime > readTime;
    
    return isUnread;
  }, [readStatus]);

  // Get the last read timestamp for a chat
  const getLastReadTime = useCallback((chatId: string): string | undefined => {
    return readStatus[chatId];
  }, [readStatus]);

  return {
    markChatAsRead,
    hasUnreadMessages,
    getLastReadTime,
    readStatus,
  };
}
